import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { Bell, Menu, MessageSquare, Moon, Search, Sun, Terminal, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useApi } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { useMe } from "@/lib/me";

const LINKS = [["/services", "Services"], ["/products", "Products"], ["/developers", "Developers"], ["/agencies", "Agencies"], ["/projects", "Projects"]];

export const Logo = () => (
  <Link to="/" className="flex items-center gap-2 font-extrabold tracking-tight" data-testid="nav-logo">
    <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary text-primary-foreground"><Terminal className="h-4 w-4" /></span>
    <span className="text-lg">Dev<span className="text-primary">Market</span></span>
  </Link>
);

export function GlobalSearch({ big = false, testId = "universal-search-input" }) {
  const api = useApi();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); return; }
    const t = setTimeout(() => api.get("/search/autocomplete", { params: { q } }).then((r) => setItems(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q, api]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const submit = (e) => { e.preventDefault(); setOpen(false); nav(`/services?q=${encodeURIComponent(q)}`); };
  return (
    <form ref={ref} onSubmit={submit} className="relative w-full">
      <Search className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground ${big ? "h-5 w-5" : "h-4 w-4"}`} />
      <input data-testid={testId} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        placeholder={big ? "Try “Next.js SaaS”, “AI agent”, “Kubernetes”…" : "Search services, sellers, products"}
        className={`w-full rounded-xl border border-border bg-background outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-4 focus:ring-primary/10 ${big ? "h-14 pl-11 pr-32 text-base" : "h-10 pl-9 pr-3 text-sm"}`} />
      {big && <Button type="submit" className="absolute right-2 top-1/2 h-10 -translate-y-1/2 rounded-lg px-5" data-testid="hero-search-submit">Search</Button>}
      {open && items.length > 0 && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-border bg-popover shadow-xl" data-testid="search-autocomplete">
          {items.map((it, i) => (
            <button type="button" key={i} onClick={() => { setOpen(false); setQ(""); nav(it.url); }} data-testid={`autocomplete-item-${i}`}
              className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-accent">
              <span className="truncate">{it.label}</span><span className="ml-3 text-xs capitalize text-muted-foreground">{it.type}</span>
            </button>
          ))}
        </div>
      )}
    </form>
  );
}

const IconLink = ({ to, icon: Icon, count, testId, label }) => (
  <Link to={to} data-testid={testId} aria-label={label} className="relative rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
    <Icon className="h-5 w-5" />
    {count > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">{count}</span>}
  </Link>
);

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { me } = useMe();
  return (
    <header className="sticky top-0 z-50 border-b border-border/80 bg-background/80 backdrop-blur-md">
      <div className="container-x flex h-16 items-center gap-6">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex">
          {LINKS.map(([to, label]) => (
            <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase()}-link`}
              className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-foreground ${isActive ? "text-foreground" : "text-muted-foreground"}`}>{label}</NavLink>
          ))}
        </nav>
        <div className="ml-auto hidden max-w-xs flex-1 md:block"><GlobalSearch testId="nav-search-input" /></div>
        <div className="ml-auto flex items-center gap-1 md:ml-0">
          <button onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme" className="rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <Show when="signed-in">
            <IconLink to="/messages" icon={MessageSquare} count={me?.unread_messages} testId="nav-messages-link" label="Messages" />
            <IconLink to="/notifications" icon={Bell} count={me?.unread_notifications} testId="nav-notifications-link" label="Notifications" />
            <Button asChild size="sm" variant="outline" className="ml-1 hidden sm:inline-flex" data-testid="nav-dashboard-link"><Link to="/dashboard"><LayoutDashboard className="mr-1.5 h-4 w-4" />Dashboard</Link></Button>
            <div className="ml-2"><UserButton /></div>
          </Show>
          <Show when="signed-out">
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="nav-sign-in"><Link to="/sign-in">Sign in</Link></Button>
            <Button asChild size="sm" data-testid="nav-sign-up"><Link to="/sign-up">Join</Link></Button>
          </Show>
          <Sheet>
            <SheetTrigger asChild><button className="rounded-lg p-2 lg:hidden" data-testid="mobile-menu-button" aria-label="Menu"><Menu className="h-5 w-5" /></button></SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetTitle className="mb-6"><Logo /></SheetTitle>
              <div className="mb-4"><GlobalSearch testId="mobile-search-input" /></div>
              <div className="flex flex-col gap-1">
                {[...LINKS, ["/categories", "Categories"], ["/pricing", "Pricing"], ["/dashboard", "Dashboard"]].map(([to, l]) => (
                  <Link key={to} to={to} className="rounded-lg px-3 py-2 font-medium hover:bg-accent" data-testid={`mobile-nav-${l.toLowerCase()}`}>{l}</Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
